import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BlockIcon from '@mui/icons-material/Block';
import User from 'types/User';
import TierChip from 'components/project/TierChip';
import { formatDate, isRecentlyCreated } from 'utils/manageMemberships';

interface AllUsersTabProps {
  users: User[];
  searchText: string;
  isMobile: boolean;
  isLoading: boolean;
  savingEmail: string | null;
  reviewCommentByEmail: Record<string, string>;
  onEdit: (user: User) => void;
}

/** Renders truncated text with a tooltip on hover for long values. */
const TruncatedCell: React.FC<{ value?: string; fallback?: string }> = ({
  value,
  fallback = '—',
}) => {
  if (!value) {
    return (
      <Typography variant="body2" color="text.disabled">
        {fallback}
      </Typography>
    );
  }
  return (
    <Tooltip title={value} arrow>
      <Typography
        variant="body2"
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {value}
      </Typography>
    </Tooltip>
  );
};

const AllUsersTab: React.FC<AllUsersTabProps> = ({
  users,
  searchText,
  isMobile,
  isLoading,
  savingEmail,
  reviewCommentByEmail,
  onEdit,
}) => {
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.2,
      minWidth: 220,
      valueGetter: (params) =>
        `${params.row.firstName || ''} ${params.row.lastName || ''}`.trim(),
      renderCell: (params: GridRenderCellParams<User>) => {
        const fullName = `${params.row.firstName} ${params.row.lastName}`;
        const status = params.row.requestStatus;
        const isUpgrade =
          status === 'UPGRADE_REQUEST_PENDING' ||
          (!status &&
            params.row.requestedTier &&
            params.row.requestedTier !== params.row.membershipTier);
        const isNew = !isUpgrade && isRecentlyCreated(params.row.createdAt);

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              overflow: 'hidden',
              width: '100%',
            }}
          >
            <Tooltip title={fullName} arrow>
              <Typography
                variant="body2"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}
              >
                {fullName}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.5,
      minWidth: 220,
      renderCell: (params: GridRenderCellParams<User>) => (
        <TruncatedCell value={params.row.email} />
      ),
    },
    {
      field: 'affiliation',
      headerName: 'Affiliation',
      flex: 1,
      minWidth: 160,
      renderCell: (params: GridRenderCellParams<User>) => (
        <TruncatedCell value={params.row.affiliation} />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.9,
      minWidth: 160,
      valueGetter: (params) => {
        const { approved, requestStatus, requestedTier, membershipTier } = params.row;
        if (approved === false) return 'disabled';
        if (
          requestStatus === 'UPGRADE_REQUEST_PENDING' ||
          requestStatus === 'INITIAL_REQUEST_PENDING' ||
          (!requestStatus && requestedTier && requestedTier !== membershipTier)
        ) return 'pending';
        return 'active';
      },
      renderCell: (params: GridRenderCellParams<User>) => {
        const { approved, membershipTier, requestedTier, requestStatus } = params.row;
        const isPending =
          requestStatus === 'UPGRADE_REQUEST_PENDING' ||
          requestStatus === 'INITIAL_REQUEST_PENDING' ||
          (!requestStatus &&
            requestedTier &&
            requestedTier !== membershipTier);
        const isDisabled = approved === false;

        if (isDisabled) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BlockIcon sx={{ color: 'error.main', fontSize: 20 }} />
              <TierChip tier={membershipTier} />
            </Box>
          );
        }
        if (isPending) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HourglassEmptyIcon sx={{ color: 'warning.main', fontSize: 20 }} />
              <TierChip tier={requestedTier || membershipTier} />
            </Box>
          );
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
            <TierChip tier={membershipTier} />
          </Box>
        );
      },
    },
    {
      field: 'memberSince',
      headerName: 'Member Since',
      flex: 0.8,
      minWidth: 150,
      valueGetter: (params) => {
        const dateStr = params.row.createdAt;
        return dateStr ? new Date(dateStr).getTime() : -Infinity;
      },
      sortComparator: (v1, v2) => (v1 as number) - (v2 as number),
      renderCell: (params: GridRenderCellParams<User>) => {
        const dateStr = params.row.createdAt;
        if (!dateStr) return <Typography variant="body2">—</Typography>;
        return (
          <Typography variant="body2">
            {new Date(dateStr).toLocaleDateString()}
          </Typography>
        );
      },
    },
    {
      field: 'adminComment',
      headerName: 'Admin Comment',
      flex: 1,
      minWidth: 160,
      sortable: false,
      valueGetter: (params) => reviewCommentByEmail[params.row.email] || '',
      renderCell: (params: GridRenderCellParams<User>) => (
        <TruncatedCell value={reviewCommentByEmail[params.row.email]} />
      ),
    },
  ];

  const renderCard = (user: User): React.ReactElement => (
    <Card key={user.email} variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Typography variant="subtitle1" fontWeight="bold">
          {user.firstName} {user.lastName}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {user.email}
        </Typography>
        {user.affiliation && (
          <Typography variant="body2" gutterBottom>
            {user.affiliation}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          {user.approved === false ? (
            <BlockIcon sx={{ color: 'error.main', fontSize: 20 }} />
          ) : (user.requestStatus === 'UPGRADE_REQUEST_PENDING' ||
              user.requestStatus === 'INITIAL_REQUEST_PENDING' ||
              (!user.requestStatus && user.requestedTier && user.requestedTier !== user.membershipTier)) ? (
            <HourglassEmptyIcon sx={{ color: 'warning.main', fontSize: 20 }} />
          ) : (
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
          )}
          <TierChip
            tier={
              (user.requestStatus === 'UPGRADE_REQUEST_PENDING' ||
                (!user.requestStatus && user.requestedTier && user.requestedTier !== user.membershipTier))
                ? user.requestedTier
                : user.membershipTier
            }
          />
        </Box>
        {user.createdAt && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Member since: {formatDate(user.createdAt)}
          </Typography>
        )}
        {reviewCommentByEmail[user.email] && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Admin Comment:
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              {reviewCommentByEmail[user.email]}
            </Typography>
          </Box>
        )}
        <Box sx={{ mt: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onEdit(user)}
            disabled={savingEmail === user.email}
          >
            {savingEmail === user.email ? 'Saving…' : 'Edit'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          mb: 1.5,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {users.length} user{users.length !== 1 ? 's' : ''}
          {searchText ? ` matching "${searchText}"` : ' total'}
        </Typography>
      </Box>
      {isMobile ? (
        <Box>
          {users.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No users found.
            </Typography>
          ) : (
            users.map(renderCard)
          )}
        </Box>
      ) : (
        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={users}
            columns={columns}
            getRowId={(row: User) => row.email}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: { sortModel: [{ field: 'memberSince', sort: 'desc' }] },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            sortingOrder={['asc', 'desc']}
            disableRowSelectionOnClick
            disableColumnMenu
            autoHeight
            density="standard"
            loading={isLoading && users.length > 0}
            onRowClick={(params) => onEdit(params.row as User)}
            sx={{
              fontSize: '0.95rem',
              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
              '& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer': {
                visibility: 'visible',
                width: 'auto',
              },
              '& .MuiDataGrid-sortIcon': { opacity: '1 !important' },
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          />
        </Box>
      )}
    </>
  );
};

export default AllUsersTab;